import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import CategoryNav from '@/pages/home/components/CategoryNav';
import { categories, videos } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredVideos = useMemo(() => {
    let result = videos.filter((v) => v.category === currentCategory.slug);
    if (query) {
      result = result.filter(
        (v) =>
          v.title.includes(query) ||
          v.tags.some((t) => t.includes(query)) ||
          v.description.includes(query)
      );
    }
    if (result.length === 0 && !query) {
      result = videos.filter((v) => v.category === currentCategory.slug);
    }
    return result;
  }, [currentCategory.slug, query]);

  const pageTitle = query
    ? `搜索"${query}" - ${currentCategory.name} - 蓝莓视频`
    : `${currentCategory.name} - 蓝莓视频 | 高清免费在线观看${currentCategory.name}全集`;

  const pageDescription = query
    ? `在蓝莓视频搜索"${query}"相关的${currentCategory.name}资源，高清免费在线观看，不缓冲流畅播放。`
    : currentCategory.description + '，蓝莓视频提供海量' + currentCategory.name + '资源，不缓冲流畅播放。';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${currentCategory.name},蓝莓视频,免费在线观看,高清,全集,不缓冲`}
        canonicalUrl={`https://lanmei.video/category/${currentCategory.slug}`}
        ogImage="https://readdy.ai/api/search-image?query=Video%20category%20collection%20warm%20amber%20cinematic%20atmosphere%20streaming%20platform%20elegant%20minimalist&width=1200&height=630&seq=og-category-default&orientation=landscape"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: currentCategory.name + ' - 蓝莓视频',
            description: pageDescription,
            url: `https://lanmei.video/category/${currentCategory.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '蓝莓视频',
              url: 'https://lanmei.video',
            },
          }),
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/dianshiju' },
                { label: currentCategory.name },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-4 mb-1 font-heading">
              {query ? `搜索"${query}" - ${currentCategory.name}` : currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">{currentCategory.description}</p>
          </div>
        </div>

        <CategoryNav activeSlug={currentCategory.slug} />

        <section className="py-6 md:py-8" data-product-shop>
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              {filteredVideos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} {...video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">
                    {query ? `未找到与"${query}"相关的内容，请尝试其他关键词` : '暂无相关内容'}
                  </p>
                </div>
              )}

              {filteredVideos.length > 0 && filteredVideos.length < videos.filter(v => v.category === currentCategory.slug).length && (
                <div className="text-center mt-8">
                  <p className="text-sm text-foreground-500">
                    显示 {filteredVideos.length} 个结果
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}